import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import { apiGet } from "../lib/api";
import type { JobSpendSnapshot } from "../lib/types";
import { getStoredJobId } from "../lib/utils";

const POLL_MS = 5000;

const EMPTY_SPEND: JobSpendSnapshot = {
  job_id: "",
  spend_usd: 0,
  budget_usd: 25,
  remaining_usd: 25,
  status: "active",
  budget_exceeded: false,
  evidence_count: 0,
  evidence_collected: 0,
  evidence_eligible: 0,
  evidence_pending: 0,
  evidence_off_target: 0,
  evidence_cap: 100,
  evidence_cap_reached: false,
  desk_locked: false,
};

function normalizeSpend(raw: Partial<JobSpendSnapshot>): JobSpendSnapshot {
  return {
    ...EMPTY_SPEND,
    ...raw,
    desk_locked: Boolean(raw.budget_exceeded || raw.status === "budget_exceeded"),
  };
}

function resolveActiveJobId(searchParams: URLSearchParams): string {
  return (
    searchParams.get("job_id") ||
    searchParams.get("job") ||
    getStoredJobId() ||
    ""
  );
}

type DeskSpendContextValue = {
  spend: JobSpendSnapshot;
  refresh: () => Promise<void>;
  loading: boolean;
};

const DeskSpendContext = createContext<DeskSpendContextValue | null>(null);

export function DeskSpendProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const activeJobId = resolveActiveJobId(searchParams);
  const [spend, setSpend] = useState<JobSpendSnapshot>(EMPTY_SPEND);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      if (activeJobId) {
        const data = await apiGet<{ spend?: JobSpendSnapshot }>(
          `/api/spend/current?job_id=${encodeURIComponent(activeJobId)}`,
        );
        if (data.spend && typeof data.spend === "object") {
          setSpend(normalizeSpend(data.spend));
          return;
        }
      }
      const state = await apiGet<{ spend?: JobSpendSnapshot }>("/api/phase1/state");
      const raw = state.spend;
      if (raw && typeof raw === "object") {
        setSpend(normalizeSpend(raw));
      }
    } catch {
      /* keep last snapshot */
    } finally {
      setLoading(false);
    }
  }, [activeJobId]);

  useEffect(() => {
    setLoading(true);
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const value = useMemo(
    () => ({ spend, refresh, loading }),
    [spend, refresh, loading],
  );

  return (
    <DeskSpendContext.Provider value={value}>{children}</DeskSpendContext.Provider>
  );
}

export function useDeskSpend(): DeskSpendContextValue {
  const ctx = useContext(DeskSpendContext);
  if (!ctx) {
    throw new Error("useDeskSpend must be used within DeskSpendProvider");
  }
  return ctx;
}
